/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
} from "@mui/material";
// import { AlarmIcon } from "../../../components/UIElements/svgIcons";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import CancelIcon from "@mui/icons-material/Cancel";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apifetchNotifications,
  clearAllNotifications,
  deleteNotification,
  updateNotification,
} from "../api/notification";
import usePageVisibility from "../hooks/usePageVisibility";
import { useAuth } from "../hooks/useAuth";
import useResponsive from "../hooks/useResponsive";
import { Link } from "react-router-dom";

const NotificationContent = () => {
  const theme = useTheme();
  const isVisible = usePageVisibility();
  const { openNotification, handleNotification, handleIsNotification } =
    useAuth();
  const intervalRef = useRef<number | null>(null);
  // const anchorRef = useRef<HTMLDivElement | null>(null);
  // const [position, setPosition] = useState({ top: 60, right: 40 });
  const isSmall = useResponsive("down", "md", "lg");
  const [notification_data, setNotification_data] = useState<
    {
      created_at: string;
      is_read: boolean;
      message: string;
      priority: string;
      receiver: string;
      reference: string;
      title: string;
      type: string;
      updated_at: string;
      _id: string;
      link?: string;
    }[]
  >([]);

  const queryClient = useQueryClient();

  const { data, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const result = await apifetchNotifications();
      if (Array.isArray(result?.data)) {
        setNotification_data(result?.data);
        if (result.data.length > 0) {
          handleIsNotification(true);
        } else {
          handleIsNotification(false);
        }
        return result?.data;
      } else {
        setNotification_data([]);
        return [];
      }
    },
    enabled: false,
  });
  useEffect(() => {
    if (isVisible) {
      refetch(); // Fetch notifications immediately
      intervalRef.current = window.setInterval(refetch, 30000); // Fetch every 30 seconds
    } else if (intervalRef.current !== null) {
      clearInterval(intervalRef.current); // Clear the interval if set
      intervalRef.current = null; // Reset to null
    }

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current); // Cleanup on unmount
        intervalRef.current = null;
      }
    };
  }, [isVisible, refetch]);

  const updateMutation = useMutation({
    mutationFn: (id: string) => updateNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"], // Target this key
        exact: true, // Ensure it matches exactly if needed
      });
      refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"], // Target this key
        exact: true, // Ensure it matches exactly if needed
      });
      refetch();
    },
  });

  const clearAllNotificationsMutation = useMutation({
    mutationFn: () => clearAllNotifications(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"], // Target this key
        exact: true, // Ensure it matches exactly if needed
      });
      refetch();
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleClearAllNotifications = () => {
    clearAllNotificationsMutation.mutate();
  };
  const handleUpdate = (id: string) => {
    updateMutation.mutate(id);
  };

  return (
    <>
      {openNotification && (
        <Box
          sx={{
            position: "fixed",
            top: "60px",
            right: "40px",
            background: "#fff",
            border: "1px solid #ccc",
            padding: "14px",
            borderRadius: "14px",
            zIndex: 90,
            width: isSmall ? "75%" : 400,
            height: 350,
            overflowY: "auto",
          }}
        >
          <Stack
            direction="row"
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{ ml: 3 }}
          >
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                textAlign: "left",
              }}
            >
              Notifications
            </Typography>
            <Stack
              direction="row"
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography
                sx={{
                  color: theme.palette.primary.dark,
                  fontWeight: 400,
                  textAlign: "left",
                  mr: 1,
                  cursor: "pointer",
                }}
                onClick={() => handleClearAllNotifications()}
              >
                clear all
              </Typography>
              <CloseIcon
                onClick={() => handleNotification(false)}
                sx={{ cursor: "pointer" }}
              />
            </Stack>
          </Stack>

          <List>
            {notification_data?.map((notification) => (
              <ListItem
                key={notification?._id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  py: 1.5,
                }}
                secondaryAction={
                  <IconButton
                    onClick={() => handleUpdate(notification?._id)}
                    edge="end"
                    aria-label="delete"
                  >
                    <CancelIcon color="error" />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 600, color: "#000" }}>
                      {notification?.title}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ fontSize: 13, color: "#555" }}>
                      {notification?.message.length > 50
                        ? notification?.message.substring(0, 50) + "..."
                        : notification?.message}
                    </Typography>
                  }
                />
                {notification?.link && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      href={notification?.link}
                      target="_blank"
                    >
                      Take Action
                    </Button>
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </>
  );
};

export default NotificationContent;
